import {
  NEAREST_WORD_THRESHOLD_PX,
  PREFIX_SUFFIX_WINDOW_CHARS,
} from '../constants'
import type {
  AnnotationMetadata,
  PDFAnchorFragment,
  PdfPageProxyLike,
  PdfRect,
  PdfViewportLike,
  SearchResult,
} from '../types'
import { EAnnotationEngine } from '../types'
import type { DomLocatorService } from '../utils/DomLocatorService'
import * as pdfCoordinateUtils from '../utils/pdfCoordinateUtils'
import { PdfOverlayRenderer } from '../utils/PdfOverlayRenderer'
import { PageRuntime, PdfTextIndexer, WordBox } from '../utils/PdfTextIndexer'

import { IPdfAnnotationEngine } from './AnnotationEngine'

type SelectionState = {
  isSelecting: boolean
  anchorGlobalIndex: number | null
  currentGlobalIndex: number | null
  pointerId: number | null
  capturedPageIndex: number | null
}

export type HighlightStyle = {
  color: string
  alpha: number
}

export class PdfAnnotationEngine implements IPdfAnnotationEngine {
  /* ---------------------------------- */
  /* Runtime state (PDF-specific)       */
  /* ---------------------------------- */

  private readonly pageRuntimes: Map<number, PageRuntime> = new Map()

  // text / index data (selection support)
  private globalWords: WordBox[] = []
  private wordKeyToGlobalIndex: Map<string, number> = new Map()
  private globalText = ''
  private globalOffsets: number[] = []

  private selectionState: SelectionState = {
    isSelecting: false,
    anchorGlobalIndex: null,
    currentGlobalIndex: null,
    pointerId: null,
    capturedPageIndex: null,
  }

  private lastSelection: AnnotationMetadata | null = null
  private readonly persistedStyle: HighlightStyle | undefined = undefined
  private activeCommentId: string | null = null

  private readonly onSelectionCommitted?: (metadata: AnnotationMetadata) => void

  constructor(options?: {
    onSelectionCommitted?: (metadata: AnnotationMetadata) => void
    persistedHighlightStyle?: HighlightStyle
  }) {
    this.onSelectionCommitted = options?.onSelectionCommitted
    this.persistedStyle = options?.persistedHighlightStyle
  }

  async registerPage(
    pageIndex: number,
    refs: {
      wrapper: HTMLDivElement
      highlightCanvas: HTMLCanvasElement
      focusHighlightCanvas?: unknown
      controlCanvas: HTMLCanvasElement
      bubbleLayer: HTMLDivElement
    },
    pageProxy: PdfPageProxyLike,
    viewport: PdfViewportLike,
    pageWidthPx: number,
    heightPx: number
  ): Promise<void> {
    const { words, pageText, wordOffsets } =
      await PdfTextIndexer.buildPageWordBoxes({
        pageProxy,
        viewport,
        pageIndex,
      })

    const runtime: PageRuntime = {
      pageIndex,
      pageProxy,
      viewport,
      pageWidthPx,
      heightPx,
      wrapperEl: refs.wrapper,
      highlightCanvas: refs.highlightCanvas,
      controlCanvas: refs.controlCanvas,
      bubbleLayerEl: refs.bubbleLayer,
      words,
      pageText,
      wordOffsets,
    }

    this.pageRuntimes.set(pageIndex, runtime)
    this.rebuildGlobalIndex()
  }

  unregisterPage(pageIndex: number): void {
    this.pageRuntimes.delete(pageIndex)
    this.rebuildGlobalIndex()
  }

  private rebuildGlobalIndex(): void {
    const result = PdfTextIndexer.buildGlobalTextIndex(this.pageRuntimes)
    this.globalWords = result.globalWords
    this.wordKeyToGlobalIndex = result.wordKeyToGlobalIndex
    this.globalText = result.text
    this.globalOffsets = result.globalWordOffsets
  }

  /**
   * Return the last committed selection metadata.
   * Controller decides when to send it to backend.
   */
  get(): AnnotationMetadata | null {
    return this.lastSelection
  }

  /**
   * Engine does not implement search yet.
   * Reserved for future (e.g. re-anchor).
   */
  search(_annotation: AnnotationMetadata): SearchResult | null {
    return null
  }

  apply(
    annotation: AnnotationMetadata,
    commentId: string,
    style?: HighlightStyle
  ): boolean {
    if (
      annotation.engine !== EAnnotationEngine.PDF ||
      !annotation.fragments ||
      annotation.fragments.length === 0
    ) {
      return false
    }

    this.clearHighlight()
    this.activeCommentId = commentId

    const fragments = annotation.fragments as PDFAnchorFragment[]
    const highlightStyle = style ||
      this.persistedStyle || {
        color: 'rgba(255, 255, 0, 0.3)',
        alpha: 1,
      }
    this.drawFragments(fragments, highlightStyle)

    return true
  }

  remove(commentId?: string): boolean {
    if (commentId && this.activeCommentId !== commentId) {
      return false
    }
    this.clearHighlight()
    this.activeCommentId = null
    return true
  }

  removeAll(): void {
    this.clearHighlight()
    this.activeCommentId = null
    this.lastSelection = null
  }

  private clearHighlight(): void {
    PdfOverlayRenderer.clearHighlightCanvasesAcrossPages(this.pageRuntimes)
  }

  private drawFragments(
    fragments: PDFAnchorFragment[],
    style: HighlightStyle
  ): void {
    for (const fragment of fragments) {
      const runtime = this.pageRuntimes.get(fragment.pageIndex)
      if (!runtime) continue

      PdfOverlayRenderer.drawHighlightRectsOnPage(
        runtime.highlightCanvas,
        runtime.viewport,
        fragment.rects,
        style
      )
    }
  }

  handlePointerDown(event: PointerEvent, pageIndex: number): void {
    const word = this.findWordUnderPointer(
      event.clientX,
      event.clientY,
      pageIndex
    )

    if (!word) return

    const runtime = this.pageRuntimes.get(pageIndex)
    if (!runtime?.controlCanvas) return

    try {
      runtime.controlCanvas.setPointerCapture(event.pointerId)
    } catch {
      // Safari / edge cases – safe to ignore
    }

    this.clearHighlight()
    this.activeCommentId = null

    this.selectionState = {
      isSelecting: true,
      anchorGlobalIndex: word.globalIndex,
      currentGlobalIndex: word.globalIndex,
      pointerId: event.pointerId,
      capturedPageIndex: pageIndex,
    }

    this.redrawSelectionHighlight()
  }

  handlePointerMove(event: PointerEvent): void {
    if (!this.selectionState.isSelecting) {
      // hover logic
      const pageInfo = this.findPageUnderPointer(event.clientX, event.clientY)
      if (!pageInfo) return

      const word = this.findWordUnderPointer(
        event.clientX,
        event.clientY,
        pageInfo.pageIndex
      )

      const runtime = this.pageRuntimes.get(pageInfo.pageIndex)
      if (runtime?.controlCanvas) {
        PdfOverlayRenderer.updateCursorStyle(
          runtime.controlCanvas,
          word ? 'text' : 'default'
        )
      }
      return
    }

    const pageInfo = this.findPageUnderPointer(event.clientX, event.clientY)
    if (!pageInfo) return

    const word = this.findWordUnderPointer(
      event.clientX,
      event.clientY,
      pageInfo.pageIndex
    )
    if (!word) return

    this.selectionState.currentGlobalIndex = word.globalIndex
    this.redrawSelectionHighlight()
  }

  handlePointerUp(_event: PointerEvent): void {
    if (!this.selectionState.isSelecting) return

    if (
      this.selectionState.capturedPageIndex !== null &&
      this.selectionState.pointerId !== null
    ) {
      const runtime = this.pageRuntimes.get(
        this.selectionState.capturedPageIndex
      )
      if (runtime?.controlCanvas) {
        try {
          runtime.controlCanvas.releasePointerCapture(
            this.selectionState.pointerId
          )
        } catch {
          // ignore
        }
      }
    }

    // store selection
    this.commitSelection()

    // reset state when mouse up?
    this.selectionState = {
      isSelecting: false,
      anchorGlobalIndex: null,
      currentGlobalIndex: null,
      pointerId: null,
      capturedPageIndex: null,
    }
  }

  handlePointerCancel(): void {
    if (!this.selectionState.isSelecting) return

    if (
      this.selectionState.capturedPageIndex !== null &&
      this.selectionState.pointerId !== null
    ) {
      const runtime = this.pageRuntimes.get(
        this.selectionState.capturedPageIndex
      )
      if (runtime?.controlCanvas) {
        try {
          runtime.controlCanvas.releasePointerCapture(
            this.selectionState.pointerId
          )
        } catch {
          // ignore
        }
      }
    }

    this.clearSelection()
  }

  clearSelection(): void {
    this.selectionState = {
      isSelecting: false,
      anchorGlobalIndex: null,
      currentGlobalIndex: null,
      pointerId: null,
      capturedPageIndex: null,
    }
    this.clearHighlight()
  }

  reset(): void {
    this.clearSelection()
    this.clearHighlight()
    this.lastSelection = null
  }

  unregisterAllPages(): void {
    for (const [pageIndex] of this.pageRuntimes) {
      this.unregisterPage(pageIndex)
    }
    this.pageRuntimes.clear()
    this.globalWords = []
    this.wordKeyToGlobalIndex.clear()
    this.globalText = ''
    this.globalOffsets = []
  }

  private findWordUnderPointer(
    clientX: number,
    clientY: number,
    pageIndex: number
  ): WordBox | null {
    const runtime = this.pageRuntimes.get(pageIndex)
    if (!runtime?.wrapperEl) {
      return null
    }

    const wrapperRect = runtime.wrapperEl.getBoundingClientRect()
    const localX = clientX - wrapperRect.left
    const localY = clientY - wrapperRect.top

    for (let localIndex = 0; localIndex < runtime.words.length; localIndex++) {
      const word = runtime.words[localIndex]
      if (pdfCoordinateUtils.pointInRect(localX, localY, word.rectVp)) {
        const key = `${pageIndex}:${localIndex}`
        const globalIndex = this.wordKeyToGlobalIndex.get(key)
        if (globalIndex !== undefined) {
          return this.globalWords[globalIndex]
        }
      }
    }

    let nearestIndex: number | null = null
    let bestDist2 = Infinity
    const threshold2 = NEAREST_WORD_THRESHOLD_PX * NEAREST_WORD_THRESHOLD_PX

    for (let localIndex = 0; localIndex < runtime.words.length; localIndex++) {
      const word = runtime.words[localIndex]
      const cx = word.rectVp.x + word.rectVp.w / 2
      const cy = word.rectVp.y + word.rectVp.h / 2
      const dx = localX - cx
      const dy = localY - cy
      const dist2 = dx * dx + dy * dy

      if (dist2 <= threshold2 && dist2 < bestDist2) {
        bestDist2 = dist2
        nearestIndex = localIndex
      }
    }

    if (nearestIndex !== null) {
      const key = `${pageIndex}:${nearestIndex}`
      const globalIndex = this.wordKeyToGlobalIndex.get(key)
      if (globalIndex !== undefined) {
        return this.globalWords[globalIndex]
      }
    }

    return null
  }

  private findPageUnderPointer(
    clientX: number,
    clientY: number
  ): { pageIndex: number } | null {
    for (const [pageIndex, runtime] of this.pageRuntimes.entries()) {
      if (!runtime.wrapperEl) continue

      const rect = runtime.wrapperEl.getBoundingClientRect()
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        return { pageIndex }
      }
    }
    return null
  }

  private redrawSelectionHighlight(): void {
    if (
      !this.selectionState.isSelecting ||
      this.selectionState.anchorGlobalIndex === null ||
      this.selectionState.currentGlobalIndex === null
    ) {
      return
    }

    const startGlobalIndex = Math.min(
      this.selectionState.anchorGlobalIndex,
      this.selectionState.currentGlobalIndex
    )
    const endGlobalIndex = Math.max(
      this.selectionState.anchorGlobalIndex,
      this.selectionState.currentGlobalIndex
    )

    const selectedWords = this.globalWords.slice(
      startGlobalIndex,
      endGlobalIndex + 1
    )

    const wordsByPage = new Map<number, WordBox[]>()
    for (const word of selectedWords) {
      if (!wordsByPage.has(word.pageIndex)) {
        wordsByPage.set(word.pageIndex, [])
      }
      wordsByPage.get(word.pageIndex)!.push(word)
    }

    this.clearHighlight()

    const style = this.persistedStyle || {
      color: 'rgba(255, 255, 0, 0.3)',
      alpha: 1,
    }

    for (const [pageIndex, words] of wordsByPage.entries()) {
      const runtime = this.pageRuntimes.get(pageIndex)
      if (!runtime) continue

      const rects = words.map((w) => ({
        leftOffset: w.rectPdf.x,
        topOffset: w.rectPdf.y,
        width: w.rectPdf.w,
        height: w.rectPdf.h,
      }))

      PdfOverlayRenderer.drawHighlightRectsOnPage(
        runtime.highlightCanvas,
        runtime.viewport,
        rects,
        style
      )
    }
  }

  private commitSelection(): void {
    if (
      this.selectionState.anchorGlobalIndex === null ||
      this.selectionState.currentGlobalIndex === null
    ) {
      return
    }

    const startGlobalIndex = Math.min(
      this.selectionState.anchorGlobalIndex,
      this.selectionState.currentGlobalIndex
    )
    const endGlobalIndex = Math.max(
      this.selectionState.anchorGlobalIndex,
      this.selectionState.currentGlobalIndex
    )

    const selectedWords = this.globalWords.slice(
      startGlobalIndex,
      endGlobalIndex + 1
    )

    const selectedText = selectedWords.map((w) => w.text).join(' ')

    const startOffset = this.globalOffsets[startGlobalIndex]
    const endOffset =
      this.globalOffsets[endGlobalIndex] +
      selectedWords[selectedWords.length - 1].text.length

    const { prefix, suffix } = pdfCoordinateUtils.extractPrefixSuffix(
      this.globalText,
      startOffset,
      endOffset,
      PREFIX_SUFFIX_WINDOW_CHARS
    )

    const wordsByPage = new Map<number, WordBox[]>()
    for (const word of selectedWords) {
      if (!wordsByPage.has(word.pageIndex)) {
        wordsByPage.set(word.pageIndex, [])
      }
      wordsByPage.get(word.pageIndex)!.push(word)
    }

    const fragments: PDFAnchorFragment[] = []
    for (const [pageIndex, words] of wordsByPage.entries()) {
      const pageText = words.map((w) => w.text).join(' ')
      const rects = words.map((w) => ({
        leftOffset: w.rectPdf.x,
        topOffset: w.rectPdf.y,
        width: w.rectPdf.w,
        height: w.rectPdf.h,
      }))

      fragments.push({
        pageIndex,
        text: pageText,
        rects,
      })
    }

    const metadata: AnnotationMetadata = {
      engine: EAnnotationEngine.PDF,
      text: selectedText,
      prefix,
      suffix,
      fragments,
    }

    this.lastSelection = metadata

    this.redrawSelectionHighlight()

    if (this.onSelectionCommitted) {
      this.onSelectionCommitted(metadata)
    }
  }

  /**
   * PDF engine does not support DOM locator.
   * Kept only to satisfy interface contract.
   */
  getDomLocator(): DomLocatorService {
    throw new Error('getDomLocator not supported for PDF annotation engine')
  }

  getAnchorClientRect(fragment: PDFAnchorFragment): DOMRect | null {
    const runtime = this.pageRuntimes.get(fragment.pageIndex)
    if (!runtime?.wrapperEl || fragment.rects.length === 0) {
      return null
    }

    const firstRect = fragment.rects[0]
    return pdfCoordinateUtils.getAnchorClientRect(
      runtime.viewport,
      runtime.wrapperEl,
      firstRect
    )
  }

  getAnchorPageRect(fragment: PDFAnchorFragment): DOMRect | null {
    const runtime = this.pageRuntimes.get(fragment.pageIndex)
    if (!runtime || fragment.rects.length === 0) return null

    const firstRect = fragment.rects[0]
    return pdfCoordinateUtils.getAnchorPageRect(runtime.viewport, {
      leftOffset: firstRect.leftOffset,
      topOffset: firstRect.topOffset,
      width: firstRect.width,
      height: firstRect.height,
    })
  }

  getBubbleLayerEl(pageIndex: number): HTMLDivElement | null {
    return this.pageRuntimes.get(pageIndex)?.bubbleLayerEl ?? null
  }

  /** Style for all keyword matches (light highlight). */
  private static readonly ALL_MATCHES_HIGHLIGHT_STYLE: HighlightStyle = {
    color: '#fff5cc',
    alpha: 0.6,
  }

  /** Style for the focused search result (sidebar focus) – darker so it stands out. */
  private static readonly FOCUS_HIGHLIGHT_STYLE: HighlightStyle = {
    color: '#FFCC33',
    alpha: 0.5,
  }

  /**
   * Draws all keyword matches in #fff5cc and the focused match (matchIndex) in #FFCC33
   * onto the given focus canvases. Clears each canvas before drawing.
   */
  drawFocusHighlight(
    keywords: string[],
    matchIndex: number,
    focusCanvasByPage: Map<number, HTMLCanvasElement | null>
  ): void {
    this.clearFocusCanvases(focusCanvasByPage)
    if (!keywords?.length || this.globalWords.length === 0) return

    const allMatchesRects = this.getAllFocusHighlightRects(keywords)
    if (allMatchesRects.length === 0) return

    allMatchesRects.forEach((rectsByPage) =>
      this.drawRectsByPage(
        rectsByPage,
        focusCanvasByPage,
        PdfAnnotationEngine.ALL_MATCHES_HIGHLIGHT_STYLE
      )
    )

    const focusRectsByPage = allMatchesRects[matchIndex]
    if (focusRectsByPage) {
      this.drawRectsByPage(
        focusRectsByPage,
        focusCanvasByPage,
        PdfAnnotationEngine.FOCUS_HIGHLIGHT_STYLE
      )
    }
  }

  private clearFocusCanvases(
    focusCanvasByPage: Map<number, HTMLCanvasElement | null>
  ): void {
    focusCanvasByPage.forEach((canvas) => {
      if (canvas) PdfOverlayRenderer.clearCanvas(canvas)
    })
  }

  private drawRectsByPage(
    rectsByPage: Array<{ pageIndex: number; rects: PdfRect[] }>,
    focusCanvasByPage: Map<number, HTMLCanvasElement | null>,
    style: HighlightStyle
  ): void {
    rectsByPage.forEach(({ pageIndex, rects }) => {
      if (rects.length === 0) return
      const canvas = focusCanvasByPage.get(pageIndex) ?? null
      const runtime = this.pageRuntimes.get(pageIndex)
      if (!canvas || !runtime?.viewport) return
      PdfOverlayRenderer.drawHighlightRectsOnPage(
        canvas,
        runtime.viewport,
        rects,
        style
      )
    })
  }

  /**
   * Returns rects (per page) for every occurrence of the search keywords.
   * Used to draw all matches with the light style, then overlay the focused one.
   */
  getAllFocusHighlightRects(
    keywords: string[]
  ): Array<Array<{ pageIndex: number; rects: PdfRect[] }>> {
    if (!keywords?.length || this.globalWords.length === 0) return []

    const results: Array<Array<{ pageIndex: number; rects: PdfRect[] }>> = []
    keywords.forEach((word) => {
      const phrase = word.trim()
      if (!phrase) return

      const textLower = this.globalText.toLowerCase()
      const phraseLower = phrase.toLowerCase()
      let pos = 0

      while (pos < this.globalText.length) {
        const found = textLower.indexOf(phraseLower, pos)
        if (found === -1) break
        const start = found
        const end = found + phrase.length
        const rectsByPage = this.getRectsForCharRange(start, end)
        if (rectsByPage.length > 0) results.push(rectsByPage)
        pos = found + 1
      }
    })

    return results
  }

  /**
   * Returns rects (per page) for the Nth occurrence of the search keywords in the PDF text.
   */
  getFocusHighlightRects(
    keywords: string[],
    matchIndex: number
  ): Array<{ pageIndex: number; rects: PdfRect[] }> {
    const all = this.getAllFocusHighlightRects(keywords)
    return all[matchIndex] ?? []
  }

  /**
   * Converts a character range [start, end) in globalText to rects per page.
   */
  private getRectsForCharRange(
    start: number,
    end: number
  ): Array<{ pageIndex: number; rects: PdfRect[] }> {
    let startWordIdx = this.findWordIndexAtOffset(start, true)
    let endWordIdx = this.findWordIndexAtOffset(end - 1, false)
    if (startWordIdx === -1 || endWordIdx === -1 || startWordIdx > endWordIdx) {
      const fallback = this.findWordRangeOverlappingOffset(start, end)
      if (!fallback) return []
      startWordIdx = fallback.startWordIdx
      endWordIdx = fallback.endWordIdx
    }
    const words = this.globalWords.slice(startWordIdx, endWordIdx + 1)
    const byPage = new Map<number, WordBox[]>()
    for (const w of words) {
      if (!byPage.has(w.pageIndex)) byPage.set(w.pageIndex, [])
      byPage.get(w.pageIndex)!.push(w)
    }
    return Array.from(byPage.entries()).map(([pageIndex, pageWords]) => ({
      pageIndex,
      rects: pageWords.map((w) => ({
        leftOffset: Number(w.rectPdf.x.toFixed(2)),
        topOffset: Number(w.rectPdf.y.toFixed(2)),
        width: Number(w.rectPdf.w.toFixed(2)),
        height: Number(w.rectPdf.h.toFixed(2)),
      })),
    }))
  }

  private findWordIndexAtOffset(charOffset: number, atStart: boolean): number {
    for (let i = 0; i < this.globalWords.length; i++) {
      const word = this.globalWords[i]
      const wordStart = this.globalOffsets[i]
      const wordEnd = wordStart + word.text.length
      if (atStart && wordStart <= charOffset && charOffset < wordEnd) return i
      if (!atStart && wordStart <= charOffset && charOffset < wordEnd) return i
    }
    return -1
  }

  /**
   * Find the range of word indices that overlap [start, end) in globalText.
   * Used when the phrase falls on spaces or when offsets don't align exactly.
   */
  private findWordRangeOverlappingOffset(
    start: number,
    end: number
  ): { startWordIdx: number; endWordIdx: number } | null {
    if (this.globalWords.length === 0) return null
    let startWordIdx = -1
    let endWordIdx = -1
    for (let i = 0; i < this.globalWords.length; i++) {
      const wordStart = this.globalOffsets[i]
      const wordEnd = wordStart + this.globalWords[i].text.length
      if (wordEnd > start && startWordIdx === -1) startWordIdx = i
      if (wordStart <= end - 1) endWordIdx = i
    }
    if (startWordIdx === -1 || endWordIdx === -1 || startWordIdx > endWordIdx)
      return null
    return { startWordIdx, endWordIdx }
  }
}
